import { translateBackend } from '@assets/i18n/config';
import { FeatureFlagTypes } from '@generated/graphql';
import type { Goal } from '@interfaces/goalSubAccountTypes';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { TableBody } from '@mui/material';
import { useAccordionTokens } from '@ovComponents/2-component/accordion/accordian.tokens';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from '@ovComponents/2-component/accordion/accordion';
import { Table } from '@ovComponents/2-component/table/table';
import { useOrganizationFeatureFlag } from '@providers/userContextProvider';
import { formatMoneyValue } from '@utils/moneyFormatting';
import { Box, Skeleton, Typography, useTheme } from 'hephaestus/web';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const goalTitles = {
  mainTitle: {
    fontWeight: 600,
  },
  description: {
    fontWeight: 400,
  },
};

const GoalCard = ({
  goal,
  onEdit,
  options,
}: {
  goal: Goal;
  onEdit?: (goal: Goal) => any;
  options: any;
}) => {
  const { t } = useTranslation(['client', 'workflowCompletions']);
  const translatedAttributesEnabled = useOrganizationFeatureFlag(
    FeatureFlagTypes.TranslatedAttributes,
  );
  const theme = useTheme();
  const tokens = useAccordionTokens(theme);
  const [expanded, setExpanded] = useState(false);
  const configuredTimeHorizon = options.timeHorizons?.find(
    (timeHorizon: any) => timeHorizon.value === goal.timeHorizon,
  );
  const configuredRiskQuestion = options.riskQuestions?.find(
    (riskQuestion: any) => riskQuestion.value === goal.riskQuestion1,
  );

  const timeHorizonLabel = () => {
    if (goal.timeHorizonLabel && translatedAttributesEnabled) {
      return translateBackend(goal.timeHorizonLabel);
    }
    const label = translateBackend(configuredTimeHorizon?.label);
    return label && label !== ''
      ? label
      : t(`client:goalTimeHorizon.${goal.timeHorizon}`);
  };

  const riskQuestionTitle = () => {
    const workflowTitle = translateBackend(options.riskQuestionTitle);
    return workflowTitle || t('workflowCompletions:goals.riskQuestion1');
  };

  const riskQuestionLabel = () => {
    if (goal.goalRiskQuestionLabel && translatedAttributesEnabled) {
      return translateBackend(goal.goalRiskQuestionLabel);
    }
    const label = translateBackend(configuredRiskQuestion?.label);
    return label && label !== ''
      ? label
      : t(
          `workflowCompletions:goals.riskQuestion1Responses.${goal.riskQuestion1}`,
        );
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      variant='workflow'
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls='panel1a-content'
        id='panel1a-header-2'
        sx={{
          '.MuiAccordionSummary-content': {
            justifyContent: 'space-between !important',
          },
          '.MuiAccordionSummary-expandIconWrapper': {
            position: 'absolute',
            top: '10px',
            right: '15px',
          },
        }}
      >
        <Box sx={{ width: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant='bodyLarge' sx={goalTitles.mainTitle}>
              {goal.name}
            </Typography>
            {options.editGoal && options.goalWorkflow !== 'default' && (
              <EditIcon
                sx={{ marginRight: '35px', fontSize: '15px' }}
                onClick={() => {
                  if (onEdit) {
                    onEdit(goal);
                  }
                }}
              />
            )}
          </Box>
          <hr
            style={{
              borderTop: `1px solid ${tokens.multipleBorderColor}`,
            }}
          ></hr>
          {!expanded && (
            <Box
              sx={{
                mt: 2,
                display: 'block',
                width: '100%',
              }}
            >
              <Table>
                <TableBody>
                  <tr>
                    <td style={{ width: '15%' }}>
                      {' '}
                      <Typography
                        variant='bodyMedium'
                        sx={[{ fontWeight: 500 }, goalTitles.mainTitle]}
                      >
                        {t('workflowCompletions:reviewGoal.goalCard.type')}
                      </Typography>
                    </td>
                    <td style={{ width: '85%' }}>
                      {' '}
                      <Typography
                        variant='bodyMedium'
                        sx={[{ fontWeight: 500 }, goalTitles.description]}
                      >
                        {t(`goalType.${goal.type}`)}
                      </Typography>
                    </td>
                  </tr>

                  {options.displayTimeHorizon && (
                    <tr>
                      <td style={{ width: '15%' }}>
                        {' '}
                        <Typography
                          variant='bodyMedium'
                          sx={[{ fontWeight: 500 }, goalTitles.mainTitle]}
                        >
                          {t('workflowCompletions:reviewGoal.goalCard.time')}
                        </Typography>
                      </td>
                      <td style={{ width: '85%' }}>
                        {' '}
                        <Typography
                          variant='bodyMedium'
                          sx={[{ fontWeight: 500 }, goalTitles.description]}
                        >
                          {timeHorizonLabel()}
                        </Typography>
                      </td>
                    </tr>
                  )}

                  {options.displayGoalTargetAmount && (
                    <tr>
                      <td style={{ width: '15%' }}>
                        {' '}
                        <Typography
                          variant='bodyMedium'
                          sx={[{ fontWeight: 500 }, goalTitles.mainTitle]}
                        >
                          {t('workflowCompletions:reviewGoal.goalCard.target')}
                        </Typography>
                      </td>
                      <td style={{ width: '85%' }}>
                        {' '}
                        <Typography
                          variant='bodyMedium'
                          sx={[{ fontWeight: 500 }, goalTitles.description]}
                        >
                          {formatMoneyValue(goal.targetAmountCents ?? 0)}
                        </Typography>
                      </td>
                    </tr>
                  )}
                </TableBody>
              </Table>
            </Box>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ mb: 2 }}>
          <Typography
            variant='bodyMedium'
            sx={[{ fontWeight: 500 }, goalTitles.mainTitle]}
          >
            {t('goalTypes')}
          </Typography>
          <Typography
            variant='bodyMedium'
            sx={[{ fontWeight: 500 }, goalTitles.description]}
          >
            {t(`goalType.${goal.type}`)}
          </Typography>
        </Box>

        {options.displayTimeHorizon && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant='bodyMedium'
              sx={[{ fontWeight: 500 }, goalTitles.mainTitle]}
            >
              {t('timeHorizon')}
            </Typography>
            <Typography
              variant='bodyMedium'
              sx={[{ fontWeight: 500 }, goalTitles.description]}
            >
              {timeHorizonLabel()}
            </Typography>
          </Box>
        )}

        {options.displayGoalTargetAmount && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant='bodyMedium'
              sx={[{ fontWeight: 500 }, goalTitles.mainTitle]}
            >
              {t('targetAmount')}
            </Typography>
            <Typography
              variant='bodyMedium'
              sx={[{ fontWeight: 500 }, goalTitles.description]}
            >
              {formatMoneyValue(goal.targetAmountCents ?? 0)}
            </Typography>
          </Box>
        )}

        {options.displayRiskAmount && (
          <Box sx={{ mb: 2 }}>
            {options.riskQuestionTitlePending ? (
              <>
                <Skeleton
                  data-testid='goal-risk-title-pending-label'
                  width={220}
                  height={20}
                />
                <Skeleton
                  data-testid='goal-risk-title-pending-value'
                  width={180}
                  height={20}
                  sx={{ mt: 0.5 }}
                />
              </>
            ) : (
              <>
                <Typography
                  variant='bodyMedium'
                  sx={[{ fontWeight: 500 }, goalTitles.mainTitle]}
                >
                  {riskQuestionTitle()}
                </Typography>
                <Typography
                  variant='bodyMedium'
                  sx={[{ fontWeight: 500 }, goalTitles.description]}
                >
                  {riskQuestionLabel()}
                </Typography>
              </>
            )}
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default GoalCard;
